import { safeboxClient } from 'myNet'
import { updateToken } from 'services/jwt'
import { unregister as unregisterTotpFingerprint } from 'services/totp/duplicateRegistry'

const ROUTE = '/v1/resources'

const ResourceService = {
  create: async (request) => {
    const response = await safeboxClient.xhrPost(`${ROUTE}`, request, null, null, {})
    const { code, message } = response.data
    if (code !== 200) {
      if (code === 401) {
        updateToken()
          .then(() => {})
          .catch(() => {
            const err = new Error(message)
            err.code = code
            throw err
          })
      } else {
        const err = new Error(message)
        err.code = code
        err.data = response.data.data
        throw err
      }
    }
    return response.data
  },
  getAll: async (request) => {
    const response = await safeboxClient.get(`${ROUTE}`, request)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  get: async ({ id, isZDW = false, ...params }) => {
    const requestParams = { ...params }
    if (isZDW === true) {
      requestParams.view = 'zdw'
    }
    const response = await safeboxClient.get(`${ROUTE}/${id}`, requestParams)
    return {
      ...response.data,
      headerAccessLevel: response.origin.headers.get('x-file-access-level'),
    }
  },
  put: async ({ id, request, sendNotification = false }) => {
    const response = await safeboxClient.xhrPut(
      `${ROUTE}/${id}?send_notification=${encodeURIComponent(sendNotification)}`,
      request,
      null,
      null,
    )
    const { code, message } = response.data
    if (code !== 200) {
      if (code === 401) {
        updateToken()
          .then(() => {})
          .catch(() => {
            const err = new Error(message)
            err.code = code
            throw err
          })
      } else {
        const err = new Error(message)
        err.code = code
        throw err
      }
    }
    return response.data
  },
  delete: async ({ id }) => {
    const response = await safeboxClient.delete(`${ROUTE}/${id}`)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    // TOTP 중복 감지 지문 제거(#1506) — 삭제된 레코드의 지문이 세션에 남아 유령
    // 배지/경고를 만드는 것을 방지. 호출처(RecordEdit/RecordDetail) 공통 chokepoint.
    unregisterTotpFingerprint(id)
    return response.data
  },
  info: async () => {
    const response = await safeboxClient.get(`${ROUTE}/info`)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  prepareShare: async (id, params) => {
    const response = await safeboxClient.post(`${ROUTE}/${id}/prepare-share`, params)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  share: async (id, params, sendNotification = false) => {
    const response = await safeboxClient.post(
      `${ROUTE}/${id}/share?send_notification=${encodeURIComponent(sendNotification)}`,
      params,
    )
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  deleteShare: async (id, params) => {
    const response = await safeboxClient.delete(`${ROUTE}/${id}/share`, params)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  download: (etag) => {
    return safeboxClient.xhrGet(`/v1/file/${etag}`)
  },
  putSharePermission: async (id, params) => {
    const response = await safeboxClient.put(`${ROUTE}/${id}/share/permission`, params)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
  bulkPutLeaked: async (data) => {
    const response = await safeboxClient.post(`${ROUTE}/leaked/bulk`, data)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }

    return response.data
  },
  getLeaked: async () => {
    const response = await safeboxClient.get(`${ROUTE}`, { leaked: true })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }

    return response.data
  },

  prepareTransferOwner: async (resourceId, params) => {
    const response = await safeboxClient.post(
      `${ROUTE}/${resourceId}/prepare-transfer-owner`,
      params,
    )
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  transferOwner: async (resourceId, params) => {
    const response = await safeboxClient.patch(`/v1/resources/${resourceId}/owner`, params)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  bulkGet: async (scope, offset = 0, limit = 100) => {
    const response = await safeboxClient.get(`${ROUTE}/bulk`, { scope, offset, limit })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return {
      ...response.data,
      fileAccessLevel: response.origin?.headers?.get('x-file-access-level') ?? null,
    }
  },

  getDownloadUrls: async (files, purpose = 'download', skipActivity = false) => {
    const query = new URLSearchParams({ purpose })
    if (skipActivity) query.set('skip_activity', 'true')
    const response = await safeboxClient.post(`${ROUTE}/files/download-urls?${query}`, {
      files,
    })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  getUploadUrls: async (files, resourceId) => {
    const url = resourceId ? `${ROUTE}/${resourceId}/upload-urls` : `${ROUTE}/upload-urls`
    const response = await safeboxClient.post(url, { files })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },

  createPresigned: async (request, params) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    const response = await safeboxClient.post(`${ROUTE}/presigned${query}`, request)
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      err.data = response.data.data
      throw err
    }
    return response.data
  },

  putPresigned: async ({ id, data, sendNotification = false }) => {
    const response = await safeboxClient.put(
      `${ROUTE}/${id}/presigned?send_notification=${sendNotification}`,
      data,
    )
    const { code, message } = response.data
    if (code !== 200) {
      if (code === 401) {
        updateToken()
          .then(() => {})
          .catch(() => {
            const err = new Error(message)
            err.code = code
            throw err
          })
      } else {
        const err = new Error(message)
        err.code = code
        throw err
      }
    }
    return response.data
  },

  shareExtensionRequest: async (resourceId, params) => {
    const response = await safeboxClient.post(
      `/v1/resources/${resourceId}/share/extension-request`,
      params,
    )
    const { code, message } = response.data

    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      err.data = response.data.data
      throw err
    }
    return response.data
  },

  bulkDelete: async (resourceIds) => {
    const response = await safeboxClient.post(`/__test/resources/delete`, {
      resource_ids: resourceIds,
    })
    const { code, message } = response.data
    if (code !== 200) {
      const err = new Error(message)
      err.code = code
      throw err
    }
    return response.data
  },
}

export default ResourceService
